import { FC, useEffect, useState } from "react";
import { ActivityType, IActivityEntry } from "@hulanbv/platformapp";
import { stylesheet } from "typestyle";
import { generatePath, useHistory } from "react-router";
import { PageBody } from "../../../elements/page-body.element";
import { Page } from "../../../elements/page.element";
import { LoadingSpinner } from "../../../elements/loading-spinner.element";
import { ValueObstacleEntryView } from "./value-obstacle-entry-view";
import { Button } from "../../../elements/button.element";
import { Flex } from "../../../elements/flex.element";
import { activityEntriesService } from "../../../../state/activities/activity-entries.service";
import { authenticationService } from "../../../../state/authentication/authentication.service";
import { routes } from "../../../../state/common/constants/routes.constants";
import { activityComponentsService } from "../../../../state/activities/activity-components.service";
import { useInviteInteractionFromQuery } from "../../../../state/exercise-invite-interactions/use-invite-interaction-from-query.hook";

const ValueObstaclesActivityScreen: FC = () => {
  const history = useHistory();
  const exerciseInviteId = useInviteInteractionFromQuery();
  const [activityState, setActivityState] = useState<
    "loading" | "playing" | "saving" | "completed" | "informing"
  >("loading");

  // get last 5 activity components from latest Value Identification session
  const [definedObstacleEntries, setDefinedObstacleEntries] = useState<
    IActivityEntry[]
  >([]);
  const [identifiedValues, setIdentifiedValues] = useState<
    IActivityEntry[] | null
  >(null);
  useEffect(() => {
    async function fetchIdentifiedValues() {
      if (!exerciseInviteId) {
        // no exercise invite id, fetch activity components and disguise them as entries
        const { data: components } = await activityComponentsService.getAll({
          match: { activityType: ActivityType.VALUE_IDENTIFICATION },
          random: true,
          limit: 5,
        });
        setIdentifiedValues(
          components.map((component) => ({
            _id: component.id,
            id: component.id,
            value: component.value,
            activityType: ActivityType.VALUE_IDENTIFICATION,
            exerciseInviteId: "",
            userId: "",
            sessionIdentifier: "",
          })),
        );
        setActivityState("informing");
        return;
      }

      // get latest value identification session ID
      const { data: valueIdentificationSession } =
        await activityEntriesService.getAll({
          distinct: "sessionIdentifier",
          match: {
            activityType: ActivityType.VALUE_IDENTIFICATION,
            userId: authenticationService.getSession()?.userId ?? "",
          },
          sort: ["-createdAt"],
          limit: 1,
        });
      const sessionIdentifier =
        valueIdentificationSession?.[0]?.sessionIdentifier ?? null;
      if (sessionIdentifier === null) {
        setIdentifiedValues([]);
        setActivityState("playing");
        return;
      }

      const { data: entries } = await activityEntriesService.getAll({
        match: { sessionIdentifier, value: { $ne: null } },
        sort: ["-createdAt"],
        limit: 5,
      });

      if (!entries || entries.length < 5) {
        setIdentifiedValues([]);
        setActivityState("playing");
        return;
      }

      setActivityState("playing");
      setIdentifiedValues(entries);
    }

    fetchIdentifiedValues();
  }, [exerciseInviteId]);

  useEffect(() => {
    async function saveValues() {
      if (exerciseInviteId && definedObstacleEntries.length) {
        await activityEntriesService.postMany({
          payload: definedObstacleEntries,
        });
      }

      setActivityState("completed");
    }

    // save the selected values when exactly 5 values have been kept
    if (
      definedObstacleEntries.length === identifiedValues?.length &&
      activityState === "playing"
    ) {
      setActivityState("saving");
      saveValues();
    }
  }, [
    activityState,
    exerciseInviteId,
    definedObstacleEntries,
    identifiedValues?.length,
  ]);

  if (
    activityState === "loading" ||
    identifiedValues === null ||
    activityState === "saving"
  ) {
    return (
      <Page
        hideGoBack
        hideProfile
        title="Identify Obstacles"
        isNavigationDisabled
      >
        <PageBody>
          <Flex flex={1} justifyContent="center" alignItems="center">
            <LoadingSpinner color="black" />
          </Flex>
        </PageBody>
      </Page>
    );
  }

  if (activityState === "informing") {
    return (
      <Page
        hideGoBack
        hideProfile
        title="Identify Obstacles"
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex flex={1} justifyContent="center" alignItems="center">
            <p style={{ textAlign: "center" }}>
              The exercise was not opened through an exercise invite. You can
              continue the exercise but the results will not be saved.
            </p>
          </Flex>
          <Button
            centerChildren
            attributes={{ onClick: () => setActivityState("playing") }}
          >
            I understand
          </Button>
        </PageBody>
      </Page>
    );
  }

  if ((identifiedValues.length ?? Number.MAX_SAFE_INTEGER) < 5) {
    return (
      <Page
        hideGoBack
        hideProfile
        title="Identify Obstacles"
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex
            flex={1}
            justifyContent="center"
            alignItems="center"
            direction="column"
          >
            <h3 style={{ textAlign: "center", margin: 0 }}>
              Insufficient Values Identified
            </h3>
            <p style={{ textAlign: "center", margin: 0 }}>
              You need to identify at least 5 values before you can define
              obstacles for them. Please finish the values card sort activity
              first.
            </p>
            <Button
              centerChildren
              attributes={{
                onClick: () =>
                  history.replace(
                    `${generatePath(
                      routes.valueIdentificationActivity.path,
                    )}?exerciseInviteId=${exerciseInviteId}`,
                  ),
              }}
            >
              Go to activity
            </Button>
          </Flex>
        </PageBody>
      </Page>
    );
  }

  if (activityState === "completed") {
    return (
      <Page
        key={definedObstacleEntries.length}
        hideGoBack
        hideProfile
        title="Identify Obstacles"
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex
            flex={1}
            justifyContent="center"
            alignItems="center"
            direction="column"
          >
            <h3 style={{ textAlign: "center", margin: 0 }}>
              Activity Completed
            </h3>
            <p style={{ textAlign: "center", margin: 0 }}>
              You have identified obstacles for all{" "}
              {identifiedValues.length ?? 0} values. Your responses have been
              saved and you can now close this activity.
            </p>
          </Flex>
          {
            // condtionally render a button to close the window if in a popup
            window.opener && (
              <Button
                centerChildren
                attributes={{ onClick: () => window.close() }}
              >
                Close
              </Button>
            )
          }
        </PageBody>
      </Page>
    );
  }

  return (
    <Page
      hideGoBack
      hideProfile
      title="Identify Obstacles"
      isNavigationDisabled
      className={styles.page}
    >
      <PageBody fullHeight>
        <ValueObstacleEntryView
          onComplete={setDefinedObstacleEntries}
          activityEntries={identifiedValues}
        />
      </PageBody>
    </Page>
  );
};

export { ValueObstaclesActivityScreen };

const styles = stylesheet({
  page: {
    background: "#fafafa",
    overflow: "hidden",
  },
});
